/**
 * TemplateMarkdownEditor — read/edit the raw template_override.md that
 * drives a structure template's AI prompt (AB#1572 D).
 *
 * Mounted via GlobalSidePanel ('templateMarkdownEdit'). For PONS-provided
 * (global) templates, saving auto-forks: a confirmation modal warns the
 * user and the save then PUTs to the same route which returns the new
 * template id.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  PiSpinnerGapLight, PiCheckCircleLight, PiWarningLight,
  PiSparkleLight, PiGitForkLight,
} from 'react-icons/pi';
import apis from '../../utils/apis';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Spinner from '../shared/TailwindComponents/Spinner';
import { readErrorMessage } from '../../utils/errorMessage';

interface TemplateMarkdownEditorProps {
  templateId: string;
  templateName?: string;
  onClose: () => void;
}

const TemplateMarkdownEditor: React.FC<TemplateMarkdownEditorProps> = ({
  templateId, templateName, onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [markdown, setMarkdown] = useState('');
  const [originalMarkdown, setOriginalMarkdown] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await apis.get(`/templates/structure/${templateId}/markdown`);
        if (!alive) return;
        const data = res?.data ?? {};
        setMarkdown(data.markdown ?? '');
        setOriginalMarkdown(data.markdown ?? '');
        setIsGlobal(!!data.is_global);
      } catch (err: any) {
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: readErrorMessage(err, t('Failed to load template markdown.')),
          showModal: true,
          duration: 3000,
        }));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [templateId, dispatch, t]);

  const dirty = useMemo(() => markdown !== originalMarkdown, [markdown, originalMarkdown]);

  const doSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await apis.put(`/templates/structure/${templateId}/markdown`, { markdown });
      const newId: string | undefined = res?.data?.id;
      const forked = newId && newId !== templateId;
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: forked ? t('Template forked into your org.') : t('Markdown saved.'),
        showModal: true,
        duration: 3000,
      }));
      setOriginalMarkdown(markdown);
      if (forked && newId) {
        // Re-route to the fork so further edits land there.
        history.push(`/templates/${newId}?type=structure`);
        onClose();
      }
    } catch (err: any) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: readErrorMessage(err, t('Failed to save template markdown.')),
        showModal: true,
        duration: 4000,
      }));
    } finally {
      setSaving(false);
    }
  }, [templateId, markdown, dispatch, t, history, onClose]);

  /*
    THE GLOBAL MODAL, not a hand-rolled one.

    This drew its own scrim and its own white card inside the panel — a second
    modal system, in a feature component, on a surface the app already owns.
    The confirmation is the app's, so it looks like every other confirmation.
  */
  const handleSaveClick = useCallback(() => {
    if (!isGlobal) {
      void doSave();
      return;
    }
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      title: t('Fork PONS template?'),
      message: t('Saving your edits creates a private copy of this PONS template under your organization.'),
      subText: t('The original PONS template is unchanged. You\'ll be redirected to the new copy.'),
      icon: <PiGitForkLight className="h-6 w-6 text-info-600" />,
      variant: 'info',
      confirmLabel: t('Create fork'),
      onConfirm: () => {
        dispatch(hideModal());
        void doSave();
      },
      onClose: () => { dispatch(hideModal()); },
    }));
  }, [isGlobal, doSave, dispatch, t]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 p-8 text-sm text-fg-muted">
        <Spinner size="glyph" className="!h-4 !w-4" />
        {t('Loading template markdown…')}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-bg">
      <div className="border-b border-hairline px-5 pb-3 pt-5">
        <h3 className="text-sm text-fg-strong">
          {t('Markdown source')}
        </h3>
        <p className="mt-1 truncate text-xs text-fg-muted">
          {templateName || templateId}
        </p>
        {isGlobal && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-info-200 bg-info-50 p-2 text-xs text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
            <PiSparkleLight className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('This is a PONS template. Saving creates an editable copy in your org — the original stays as-is.')}</span>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden p-5">
        <Textarea
          id="template-markdown"
          label=""
          rows={28}
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          placeholder={t('# Template title\n\n## Section name\nSection prompt or guidance…')}
        />
      </div>

      <div className="glass-bar flex items-center justify-between gap-2 border-t border-hairline px-5 py-4">
        <div>
          {dirty ? (
            <span className="inline-flex items-center gap-1 text-xs text-warning-600">
              <PiWarningLight className="h-3.5 w-3.5" /> {t('Unsaved changes')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-success-600">
              <PiCheckCircleLight className="h-3.5 w-3.5" /> {t('Saved')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="neutral" size="small" onClick={onClose}>
            {t('Close')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleSaveClick}
            disabled={!dirty || saving}
          >
            {saving ? t('Saving…') : isGlobal ? t('Save as fork') : t('Save')}
          </Button>
        </div>
      </div>

    </div>
  );
};

export default TemplateMarkdownEditor;
